import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth.store.js";
import { ADMIN_SECTIONS, findSection } from "./adminSections.js";
import { AdminOverview } from "./AdminOverview.js";
import { AllgemeinSection } from "./sections/AllgemeinSection.js";
import { FirmenprofilSection } from "./sections/FirmenprofilSection.js";
import { KiSection } from "./sections/KiSection.js";
import { SicherheitSection } from "./sections/SicherheitSection.js";
import { SystemstatusSection } from "./sections/SystemstatusSection.js";

const SETTINGS_SECTIONS = ADMIN_SECTIONS.filter((s) => s.kind === "settings");

function renderSettings(id: string) {
  switch (id) {
    case "allgemein": return <AllgemeinSection />;
    case "firmenprofil": return <FirmenprofilSection />;
    case "ki": return <KiSection />;
    case "sicherheit": return <SicherheitSection />;
    default: return null;
  }
}

function NoAccess({ role }: { role?: string | undefined }) {
  return (
    <div>
      <div className="page-header"><h1 className="page-title">Administration</h1></div>
      <div className="card" style={{ maxWidth: 560 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>🔒 Kein Zugriff</div>
        <div style={{ fontSize: 14, color: "var(--color-text-muted)" }}>
          Das Administrationsmodul ist Administratoren vorbehalten. Deine Rolle: <strong>{role ?? "—"}</strong>.
        </div>
      </div>
    </div>
  );
}

export function AdminPage() {
  const { user } = useAuthStore();
  const { section: sectionId } = useParams<{ section?: string }>();
  const navigate = useNavigate();

  // RBAC (UI): Nur Administratoren. Der Server erzwingt zusätzlich 403 (admin = write,
  // admin/management = read).
  if (user?.role !== "admin") return <NoAccess role={user?.role} />;

  const header = (
    <div className="page-header">
      <h1 className="page-title">Administration</h1>
      <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
        Zentrale Konfiguration · {ADMIN_SECTIONS.length} Bereiche
      </div>
    </div>
  );

  // Übersicht (Admin-Center-Kartenraster).
  if (!sectionId) {
    return <div>{header}<AdminOverview /></div>;
  }

  const active = findSection(sectionId);
  if (!active) return <Navigate to="/admin" replace />;
  // Module werden direkt ins bestehende Modul deep-gelinkt (keine Doppel-UI).
  if (active.kind === "module" && active.linkPath) return <Navigate to={active.linkPath} replace />;
  // Geplante Bereiche haben keine klickbare Seite.
  if (active.kind === "planned") return <Navigate to="/admin" replace />;

  return (
    <div>
      {header}
      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
        {/* Sub-Navigation: Übersicht + die 4 produktiven Einstellungsbereiche */}
        <nav style={{ width: 220, flexShrink: 0 }}>
          <button
            onClick={() => navigate("/admin")}
            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", marginBottom: 8, borderRadius: 6, background: "transparent", color: "var(--color-text-muted)", fontSize: 13, textAlign: "left", border: "none", cursor: "pointer" }}
          >
            ← Übersicht
          </button>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--color-text-muted)", padding: "0 10px 6px" }}>
            Einstellungen
          </div>
          {SETTINGS_SECTIONS.map((s) => {
            const isActive = s.id === active.id;
            return (
              <button
                key={s.id}
                onClick={() => navigate(`/admin/${s.id}`)}
                style={{
                  display: "flex", alignItems: "center", gap: 9, width: "100%",
                  padding: "8px 10px", marginBottom: 1, borderRadius: 6,
                  background: isActive ? "var(--color-primary)" : "transparent",
                  color: isActive ? "#fff" : "var(--color-text)",
                  fontSize: 13.5, fontWeight: isActive ? 600 : 400,
                  textAlign: "left", border: "none", cursor: "pointer",
                }}
              >
                <span style={{ fontSize: 15, width: 18, textAlign: "center" }}>{s.icon}</span>
                {s.label}
              </button>
            );
          })}
        </nav>

        <section style={{ flex: 1, minWidth: 0 }}>
          {active.kind === "status" ? <SystemstatusSection /> : renderSettings(active.id)}
        </section>
      </div>
    </div>
  );
}
